import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Tag } from "lucide-react";
import CTABand from "@/components/CTABand";
import SpecTable from "@/components/SpecTable";
import { PRODUCT_CATEGORIES } from "@/data/products";

export default function ProductCategory() {
  const { slug } = useParams();
  const category = PRODUCT_CATEGORIES.find((c) => c.slug === slug);
  if (!category) return <Navigate to="/products" replace />;

  const index = PRODUCT_CATEGORIES.findIndex((c) => c.slug === slug);
  const prev = PRODUCT_CATEGORIES[(index - 1 + PRODUCT_CATEGORIES.length) % PRODUCT_CATEGORIES.length];
  const next = PRODUCT_CATEGORIES[(index + 1) % PRODUCT_CATEGORIES.length];

  const { title, short, brands, Icon, image, highlights, tables } = category;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${image})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-nago relative py-20 sm:py-24">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-red-300 hover:text-white text-sm font-semibold"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all products
          </Link>
          <div className="mt-6 flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 border border-white/20 flex items-center justify-center rounded-xl">
              <Icon className="w-7 h-7 text-red-300" />
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.24em] text-red-300">
              Product category
            </div>
          </div>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl leading-tight max-w-4xl">
            {title}
          </h1>
          <p className="mt-5 text-slate-200 max-w-3xl leading-relaxed">{short}</p>
          {brands?.length ? (
            <div className="mt-6 flex flex-wrap gap-2">
              {brands.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-3 py-1.5 text-xs font-semibold tracking-wide"
                >
                  <Tag className="w-3 h-3" /> {b}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* Highlights */}
      <section className="section bg-white">
        <div className="container-nago grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="eyebrow">Highlights</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">
              What we supply
            </h2>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {highlights?.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <div className="mt-1 w-5 h-5 shrink-0 bg-[#E30613] text-white flex items-center justify-center rounded-xl">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-slate-700 text-sm">{h}</span>
                </li>
              ))}
            </ul>

            {tables && tables.length > 0 && (
              <div className="mt-10 space-y-8" data-testid="spec-tables">
                {tables.map((t) => (
                  <SpecTable key={t.title} {...t} />
                ))}
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-slate-50 border border-slate-200 rounded-xl p-6">
              <div className="eyebrow">Need a specific grade?</div>
              <h3 className="mt-2 font-display font-bold text-xl text-slate-900">
                We stock &amp; source across all standard and specialty sizes.
              </h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Share your requirement — product, grade, size and quantity — and our team will
                get back to you with the right pack and the right price.
              </p>
              <Link to="/contact" className="btn-primary w-full mt-5" data-testid="category-quote-btn">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Prev / Next */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="container-nago py-8 grid sm:grid-cols-2 gap-4">
          <Link
            to={`/products/${prev.slug}`}
            className="group flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-xl hover:border-[#1E3A8A] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-[#E30613]" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Previous</div>
              <div className="mt-1 font-display font-semibold text-slate-900">{prev.title}</div>
            </div>
          </Link>
          <Link
            to={`/products/${next.slug}`}
            className="group flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-xl hover:border-[#1E3A8A] transition-colors sm:justify-end sm:text-right"
          >
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Next</div>
              <div className="mt-1 font-display font-semibold text-slate-900">{next.title}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#E30613]" />
          </Link>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
